// utils/scheduleSlider.js
import {createSwiperWithNav} from './swiperFactory.js';
import {isTouchDevice} from './device.js';
import {startRandomWiggle, stopRandomWiggle} from '../alpine/randomWiggle.js';

/**
 * スケジュールスライダー初期化
 * @param {string} containerSel 例: '.schedule-slider'
 * @param {string} nextBtnSel    例: '.schedule-slider-next'
 * @param {string} prevBtnSel    例: '.schedule-slider-prev'
 * @param {object} overrides     Swiperのオプション上書き
 * @returns {Swiper}
 */
export function initScheduleSlider(containerSel, nextBtnSel, prevBtnSel, overrides = {}) {
    const defaults = {
        autoplay: true,
        slidesPerView: 3,
        speed: 1500,
        breakpoints: {
            0:    { slidesPerView: 1 },
            768:  { slidesPerView: 2 },
            1024: { slidesPerView: 3 },
        },
        on: {
            init() {
                handleScheduleSlideChange(this);
                registerNavPressWatcher(this);
            },
            slideChangeTransitionEnd() {
                handleScheduleSlideChange(this);
            },
            slideChangeTransitionStart() {
                handleScheduleSlideStart(this);
            },
        },
    };

    // ネストされたキー（on/breakpoints）だけ丁寧にマージ
    const options = {
        ...defaults,
        ...overrides,
        on: { ...defaults.on, ...(overrides.on || {}) },
        breakpoints: { ...defaults.breakpoints, ...(overrides.breakpoints || {}) },
    };

    return createSwiperWithNav(containerSel, nextBtnSel, prevBtnSel, options);
}

/**
 * スケジュール：スライド開始時の処理
 */
function handleScheduleSlideStart(swiper) {
    const len = swiper.slides.length;
    if (!len) return;

    if (swiper.lastNavBtn) {
        swiper.lastNavBtn.classList.add('is-swiper-sliding');
    }

    // 直前の「ターゲット」を算出して明示的に止める
    const prevTargetIdx = getTargetIndexFor(swiper, swiper.previousIndex);
    const prevTarget = swiper.slides[prevTargetIdx];
    if (prevTarget) {
        const icon = prevTarget.querySelector('.buy-ticket-btn .icon');
        if (icon) stopRandomWiggle(icon);
        prevTarget.classList.remove('is-hover');
    }

    // 念のため取りこぼし掃除（ループ複製や連打対策）
    swiper.el.querySelectorAll('.swiper-slide.is-hover').forEach(el => {
        if (el !== prevTarget) el.classList.remove('is-hover');
    });
}

/**
 * スケジュール：スライド変更後の処理
 */
function handleScheduleSlideChange(swiper) {
    const len = swiper.slides.length;
    if (!len) return;

    const targetIndex = getTargetIndexFor(swiper, swiper.activeIndex);
    const targetSlide = swiper.slides[targetIndex];
    if (!targetSlide) return;

    // 念のため一旦全掃除してから付与（好みで省略可）
    swiper.el.querySelectorAll('.swiper-slide.is-hover').forEach(el => {
        if (el !== targetSlide) el.classList.remove('is-hover');
    });

    const icon = targetSlide.querySelector('.buy-ticket-btn .icon');
    if (icon) startRandomWiggle(icon);

    // 下から出現のトリガ（デバイス共通）
    targetSlide.classList.add('is-hover');

    // シャイン演出はタッチ端末のみ
    if (isTouchDevice()) {
        const shineTarget = targetSlide.querySelector('.shine-effect');
        if (shineTarget) {
            shineTarget.classList.remove('slide-shine', 'reset-shine');
            void shineTarget.offsetWidth;
            shineTarget.classList.add('slide-shine');
            const onEnd = () => {
                shineTarget.classList.remove('slide-shine');
                shineTarget.classList.add('reset-shine');
                shineTarget.removeEventListener('transitionend', onEnd);
            };
            shineTarget.addEventListener('transitionend', onEnd);
        }

        if (swiper.lastNavBtn) {
            swiper.lastNavBtn.classList.remove('is-swiper-sliding');
            swiper.lastNavBtn = null;
        }
    }
}

// 安全なインデックス正規化
function normalizeIndex(i, len) {
    return ((i % len) + len) % len;
}

// いまの slidesPerView を取得（'auto' 対応も一応）
function getCurrentSpv(swiper) {
    const spv = swiper.params.slidesPerView;
    if (spv === 'auto') return Math.round(swiper.slidesPerViewDynamic());
    return typeof spv === 'number' ? spv : 1;
}

// 基準インデックスから「ターゲット」を算出する共通関数
function getTargetIndexFor(swiper, baseIndex) {
    const len = swiper.slides.length;
    const spv = getCurrentSpv(swiper);
    const idx = spv === 3 ? baseIndex + 1 : baseIndex; // 3枚なら中央、他は左端
    return normalizeIndex(idx, len);
}

/**
 * ナビゲーションボタン押下監視（2重登録防止）
 */
export function registerNavPressWatcher(swiper) {
    if (swiper._navWatcher) return;
    swiper._navWatcher = true;

    const { prevEl, nextEl } = swiper.navigation;
    const remember = (btn) => () => { swiper.lastNavBtn = btn; };

    prevEl.addEventListener('pointerdown', remember(prevEl), { passive: true });
    nextEl.addEventListener('pointerdown', remember(nextEl), { passive: true });
}
